import React, { useState, useRef } from 'react';
import {
  MdUpload, MdClose, MdCheckCircle, MdError,
  MdWarning, MdRefresh, MdFilePresent, MdTableChart,
  MdExpandMore, MdExpandLess
} from 'react-icons/md';
import { FaFileExcel } from 'react-icons/fa';
import './BulkUpload.css';

/**
 * Excel Bulk Upload Component for Job Management
 * Handles Excel files with "JR Sample" and "Pipeline Sample" sheets
 *
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {Function} props.onUpload - Async function to handle file upload
 * @param {Function} props.onClose - Function to close the modal
 */
function ExcelBulkUpload({
  title = 'Bulk Upload from Excel',
  onUpload,
  onClose
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'confirm', 'result'
  const [error, setError] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);

  // Helper to categorize errors by type (JR vs Candidate)
  const categorizeErrors = (errors) => {
    if (!errors || !Array.isArray(errors)) return { jr: [], candidate: [], other: [] };

    const categorized = { jr: [], candidate: [], other: [] };
    errors.forEach(err => {
      if (err.startsWith('JR Row') || err.includes('JR Row')) {
        categorized.jr.push(err);
      } else if (err.startsWith('Candidate Row') || err.includes('Candidate Row')) {
        categorized.candidate.push(err);
      } else {
        categorized.other.push(err);
      }
    });
    return categorized;
  };

  // Expected sheets and columns info
  const templateInfo = {
    sheets: [
      {
        name: 'JR Sample',
        description: 'Job Requisitions data',
        columns: [
          'S.No', 'Vertical', 'Req id', 'Approved in WD', 'Req Approval Date', 'SLA',
          'Evergreen', 'Ageing', 'JO Closure Date', 'TAT', 'Location', 'Revised Location',
          'Exp/Fresher', 'HR Lead', 'Hiring Manager', 'BU Head', 'Hiring Type',
          'Cost Center', 'Product Line', 'Project (IND)', 'Local Cost Center (IND)',
          'Designation', 'Specialty', 'H.B./P.B', 'EMR', 'Req status',
          'Original Demand', 'Joined (till date)', 'Current Open', 'Offered/Yet to offer'
        ]
      },
      {
        name: 'Pipeline Sample',
        description: 'Candidates pipeline data',
        columns: [
          'S.No', 'HIM/RCM/Support/IT', 'DOI', 'DOJ', 'Candidate ID', 'Candidate Name',
          'Designation', 'Fresher/Experience', 'Process', 'Speciality', 'BU Lead', 'BU Head',
          'Project', 'Work Location', 'Contact No', 'Email', 'Address', 'Recruiter',
          'Source', 'Sub Source', 'Assessment Score', 'V&A interviewer', 'Ops Interviewer',
          'Offer Status', 'HB / PB', 'Host System', 'SPOC', 'UAN Check', 'UAN - Date',
          'Last Company/Current Company', 'Re-hire', 'WFH/WFO', 'Evergreen Requisition',
          'Actual Requisition'
        ]
      }
    ]
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Check file extension - support both Excel and CSV
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExt)) {
      setError('Please upload an Excel (.xlsx, .xls) or CSV (.csv) file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setUploadResult(null);
    setStep('confirm');
  };

  // Handle upload
  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const result = await onUpload(file);
      // Handle both success and partial success
      const responseData = result?.data || result || {};
      setUploadResult({
        success: result?.success !== false,
        partialSuccess: result?.partialSuccess || responseData?.partialSuccess || false,
        message: result?.message || responseData?.message || 'Upload completed successfully',
        data: {
          requisitionsCreated: responseData?.requisitionsCreated || responseData?.jrCreated || 0,
          requisitionsUpdated: responseData?.requisitionsUpdated || responseData?.jrUpdated || 0,
          candidatesCreated: responseData?.candidatesCreated || 0,
          candidatesUpdated: responseData?.candidatesUpdated || 0,
          jrAutoAssigned: responseData?.jrAutoAssigned || 0,
          errors: responseData?.errors || []
        }
      });
      setStep('result');
    } catch (err) {
      // Extract error details from response - try multiple paths
      const errorResponse = err.response?.data;
      const errorData = errorResponse?.data || {};
      const errorMessage = errorResponse?.error || errorResponse?.message || err.message || 'Upload failed';

      // Even on error, there might be partial data if backend sent it
      const hasPartialData = (errorData?.requisitionsCreated || 0) +
                             (errorData?.candidatesCreated || 0) +
                             (errorData?.jrCreated || 0) > 0;

      setUploadResult({
        success: false,
        partialSuccess: hasPartialData,
        message: errorMessage,
        data: {
          requisitionsCreated: errorData?.requisitionsCreated || errorData?.jrCreated || 0,
          requisitionsUpdated: errorData?.requisitionsUpdated || errorData?.jrUpdated || 0,
          candidatesCreated: errorData?.candidatesCreated || 0,
          candidatesUpdated: errorData?.candidatesUpdated || 0,
          jrAutoAssigned: errorData?.jrAutoAssigned || 0,
          errors: errorData?.errors || []
        }
      });
      setStep('result');
    } finally {
      setIsUploading(false);
    }
  };

  // Reset to initial state
  const handleReset = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-upload-modal excel-upload-modal">
        <div className="modal-header">
          <h3><FaFileExcel style={{ color: '#217346', marginRight: '8px' }} />{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="bulk-upload-content">
          {step === 'upload' && (
            <>
              <div className="upload-instructions">
                <h4>Upload Data File</h4>
                <p>Upload an Excel (.xlsx, .xls) or CSV (.csv) file containing Job Requisitions and/or Candidates data.</p>

                <div className="excel-sheets-info">
                  <h5><MdTableChart /> Expected Sheets:</h5>
                  {templateInfo.sheets.map((sheet, idx) => (
                    <div key={idx} className="sheet-info">
                      <div className="sheet-name">
                        <strong>{sheet.name}</strong> - {sheet.description}
                      </div>
                      <div className="sheet-columns">
                        <span className="columns-label">Columns ({sheet.columns.length}):</span>
                        <div className="columns-list">
                          {sheet.columns.slice(0, 8).map((col, i) => (
                            <span key={i} className="column-tag">{col}</span>
                          ))}
                          {sheet.columns.length > 8 && (
                            <span className="column-more">+{sheet.columns.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="upload-notes">
                  <h5>Notes:</h5>
                  <ul>
                    <li><strong>Flexible format:</strong> The system will auto-detect data type from column headers</li>
                    <li><strong>CSV files:</strong> Will be processed as Candidates data (one sheet)</li>
                    <li><strong>Excel files:</strong> Can contain JRs only, Candidates only, or both sheets</li>
                    <li>The system will automatically create missing master data (Verticals, Designations, etc.)</li>
                    <li>Existing records will be updated based on Req ID or Candidate ID</li>
                    <li>First row should contain column headers</li>
                    <li>Even if some rows fail, successful rows will still be imported</li>
                  </ul>
                </div>
              </div>

              <div
                className="drop-zone excel-drop-zone"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaFileExcel className="drop-icon excel-icon" />
                <p>Click to select or drag and drop a file</p>
                <span className="file-types">.xlsx, .xls, .csv files supported</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>

              {error && (
                <div className="error-list">
                  <h5><MdError /> Error</h5>
                  <p>{error}</p>
                </div>
              )}
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className="preview-header">
                <div className="file-info">
                  <FaFileExcel style={{ color: '#217346', fontSize: '24px' }} />
                  <span className="file-name">{file?.name}</span>
                  <span className="file-size">
                    ({(file?.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button className="btn-reset" onClick={handleReset}>
                  <MdRefresh /> Choose Different File
                </button>
              </div>

              <div className="confirm-section">
                <div className="confirm-icon">
                  <MdWarning style={{ color: '#f59e0b', fontSize: '48px' }} />
                </div>
                <h4>Ready to Upload</h4>
                <p>
                  This will process the file and import data into the system.
                  The following actions will be performed:
                </p>
                <ul className="action-list">
                  {file?.name?.endsWith('.csv') ? (
                    <>
                      <li>CSV file will be processed as Candidates data</li>
                      <li>Candidates will be created/updated based on "Candidate ID" column</li>
                    </>
                  ) : (
                    <>
                      <li>Job Requisitions from "JR Sample" sheet will be created/updated</li>
                      <li>Candidates from "Pipeline Sample" sheet will be created/updated</li>
                    </>
                  )}
                  <li>Missing master data (Verticals, Designations, etc.) will be auto-created</li>
                  <li>Candidates will be linked to Job Requisitions by "Actual Requisition" column</li>
                </ul>
              </div>

              <div className="upload-actions">
                <button className="btn-cancel" onClick={handleReset}>
                  Cancel
                </button>
                <button
                  className="btn-upload excel-btn"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner"></span>
                      Processing Excel...
                    </>
                  ) : (
                    <>
                      <MdUpload /> Upload & Process
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {step === 'result' && (
            <div className="result-container">
              {/* Determine display mode: success, partial, or error */}
              {(() => {
                const hasData = uploadResult?.data &&
                  ((uploadResult.data.requisitionsCreated || 0) +
                   (uploadResult.data.requisitionsUpdated || 0) +
                   (uploadResult.data.candidatesCreated || 0) +
                   (uploadResult.data.candidatesUpdated || 0)) > 0;
                const hasErrors = uploadResult?.data?.errors?.length > 0;
                const isPartialSuccess = uploadResult?.partialSuccess || (hasData && hasErrors);
                const isFullSuccess = uploadResult?.success && !hasErrors;

                return (
                  <>
                    {/* Icon and Title */}
                    {isFullSuccess ? (
                      <>
                        <div className="result-icon success">
                          <MdCheckCircle />
                        </div>
                        <h4>Upload Successful!</h4>
                      </>
                    ) : isPartialSuccess || hasData ? (
                      <>
                        <div className="result-icon warning">
                          <MdWarning style={{ color: '#f59e0b' }} />
                        </div>
                        <h4>Upload Completed with Issues</h4>
                      </>
                    ) : (
                      <>
                        <div className="result-icon error">
                          <MdError />
                        </div>
                        <h4>Upload Failed</h4>
                      </>
                    )}

                    <p>{uploadResult?.message}</p>

                    {/* Always show data if available */}
                    {uploadResult?.data && (
                      <div className="result-details">
                        {/* Success stats */}
                        {(uploadResult.data.requisitionsCreated > 0 || uploadResult.data.requisitionsUpdated > 0 ||
                          uploadResult.data.candidatesCreated > 0 || uploadResult.data.candidatesUpdated > 0) && (
                          <div className="success-stats">
                            <h5 style={{ color: '#16a34a', marginBottom: '8px' }}>
                              <MdCheckCircle style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              Successfully Processed
                            </h5>
                            {(uploadResult.data.requisitionsCreated > 0 || uploadResult.data.requisitionsUpdated > 0) && (
                              <div className="stat-group">
                                <div className="result-stat">
                                  <span className="stat-label">JRs Created:</span>
                                  <span className="stat-value success">{uploadResult.data.requisitionsCreated || 0}</span>
                                </div>
                                <div className="result-stat">
                                  <span className="stat-label">JRs Updated:</span>
                                  <span className="stat-value success">{uploadResult.data.requisitionsUpdated || 0}</span>
                                </div>
                                {uploadResult.data.jrAutoAssigned > 0 && (
                                  <div className="result-stat">
                                    <span className="stat-label">JRs Auto-Assigned:</span>
                                    <span className="stat-value success">{uploadResult.data.jrAutoAssigned}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            {(uploadResult.data.candidatesCreated > 0 || uploadResult.data.candidatesUpdated > 0) && (
                              <div className="stat-group">
                                <div className="result-stat">
                                  <span className="stat-label">Candidates Created:</span>
                                  <span className="stat-value success">{uploadResult.data.candidatesCreated || 0}</span>
                                </div>
                                <div className="result-stat">
                                  <span className="stat-label">Candidates Updated:</span>
                                  <span className="stat-value success">{uploadResult.data.candidatesUpdated || 0}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Error section */}
                        {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                          <div className="result-errors-section">
                            <div
                              className="errors-header"
                              onClick={() => setShowAllErrors(!showAllErrors)}
                            >
                              <h5>
                                <MdError style={{ color: '#dc2626' }} />
                                <span>Failed Rows ({uploadResult.data.errors.length})</span>
                              </h5>
                              <button className="expand-btn">
                                {showAllErrors ? <MdExpandLess /> : <MdExpandMore />}
                                {showAllErrors ? 'Collapse' : 'View All'}
                              </button>
                            </div>

                            {(() => {
                              const categorized = categorizeErrors(uploadResult.data.errors);
                              const displayErrors = showAllErrors
                                ? uploadResult.data.errors
                                : uploadResult.data.errors.slice(0, 5);

                              return (
                                <div className={`errors-content ${showAllErrors ? 'expanded' : ''}`}>
                                  {/* Show categorized summary */}
                                  <div className="errors-summary">
                                    {categorized.jr.length > 0 && (
                                      <span className="error-category jr">
                                        JR Errors: {categorized.jr.length}
                                      </span>
                                    )}
                                    {categorized.candidate.length > 0 && (
                                      <span className="error-category candidate">
                                        Candidate Errors: {categorized.candidate.length}
                                      </span>
                                    )}
                                    {categorized.other.length > 0 && (
                                      <span className="error-category other">
                                        Other: {categorized.other.length}
                                      </span>
                                    )}
                                  </div>

                                  {/* Error list */}
                                  <div className="errors-list-container">
                                    <ul className="errors-list">
                                      {displayErrors.map((err, idx) => {
                                        // Parse the error to highlight the row info
                                        const match = err.match(/^((?:JR|Candidate|Note).*?Row \d+.*?) - (.+)$/);
                                        if (match) {
                                          return (
                                            <li key={idx} className="error-item">
                                              <span className="error-row-id">{match[1]}</span>
                                              <span className="error-message">{match[2]}</span>
                                            </li>
                                          );
                                        }
                                        // Handle "Note:" messages differently
                                        if (err.startsWith('Note:')) {
                                          return (
                                            <li key={idx} className="error-item note-item">
                                              <span className="error-message note">{err}</span>
                                            </li>
                                          );
                                        }
                                        return (
                                          <li key={idx} className="error-item">
                                            <span className="error-message">{err}</span>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                    {!showAllErrors && uploadResult.data.errors.length > 5 && (
                                      <div className="errors-more">
                                        Click "View All" to see {uploadResult.data.errors.length - 5} more errors
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {/* No data message */}
                        {!hasData && !hasErrors && (
                          <div className="no-data-message">
                            <p>No records were processed. Please check your Excel file format.</p>
                            <ul>
                              <li>Ensure your file has data rows (not just headers)</li>
                              <li>Check that column headers match expected format</li>
                              <li>Required columns: Candidate ID, Candidate Name for candidates</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="result-actions">
                <button className="btn-reset" onClick={handleReset}>
                  Upload Another File
                </button>
                <button className="btn-done" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExcelBulkUpload;
